/**
 * Mobile Price Comparison view — a virtualised card list.
 *
 * The desktop table's six sticky columns alone are wider than a phone
 * viewport (941px before the first competitor column), so below the
 * `md` breakpoint ComparisonPage mounts this instead. Same data, same
 * filters, same API — a pure alternative renderer of the enriched
 * comparison rows.
 *
 * Each collapsed card shows product / SKU / Ours / Min / Max; tapping
 * a card expands a grid of every competitor's price using the exact
 * cell components the desktop table renders, so colour coding and
 * OOS / Error / N/A semantics stay identical.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  CompetitorPriceCell,
  KeyLineBadge,
  MinMaxPriceCell,
  OurPriceCell,
} from './comparisonCells';
import { parsePrice } from '../utils/priceFormat';
import { domainName } from '../utils/domains';
import {
  getScrollPosition,
  saveScrollPosition,
} from '../utils/scrollMemory';

/** Scroll-memory key — distinct from the desktop table's 'comparison'. */
const SCROLL_MEMORY_KEY = 'comparison-mobile';

/** Estimated collapsed-card height (px) for the dynamic virtualiser. */
const CARD_ESTIMATE = 108;

/** Sort options mirroring the desktop table's sortable columns. */
const SORT_OPTIONS = [
  { value: 'product_name', label: 'Product' },
  { value: 'our_sku', label: 'SKU' },
  { value: 'our_price', label: 'Our price' },
  { value: 'min_competitor', label: 'Min price' },
  { value: 'max_competitor', label: 'Max price' },
];

/**
 * Find which competitor a min/max price belongs to.
 *
 * The API payload carries the min/max value but not its domain, so
 * scan the row's competitor prices for the first available price that
 * matches (ties: first wins — fine for an internal tool).
 *
 * @param {object} row - A comparison row.
 * @param {object|null} priceStatus - The row's min or max PriceStatus.
 * @returns {string|null} Matching domain_id, or null.
 */
function findDomainForPrice(row, priceStatus) {
  const target = parsePrice(priceStatus?.value);
  if (target == null) return null;
  for (const [domainId, ps] of Object.entries(row.competitor_prices || {})) {
    if (ps?.status === 'available' && parsePrice(ps.value) === target) {
      return domainId;
    }
  }
  return null;
}

/** Count competitors with an available price on this row. */
function pricedCount(row) {
  return Object.values(row.competitor_prices || {}).filter(
    (ps) => ps?.status === 'available' && parsePrice(ps.value) != null,
  ).length;
}

/**
 * One product card.
 *
 * @param {object} props
 * @param {object} props.row - Enriched comparison row.
 * @param {string[]} props.competitors - Ordered domain ids to show
 *   when expanded.
 * @param {boolean} props.expanded - Whether the competitor grid shows.
 * @param {() => void} props.onToggle - Expand/collapse handler.
 */
function ProductCard({ row, competitors, expanded, onToggle }) {
  const ourVal = parsePrice(row.our_price?.value);
  const minDomain = findDomainForPrice(row, row.min_competitor);
  const priced = pricedCount(row);

  return (
    <div
      className="border-b border-gray-200 bg-white px-3 py-2.5
                 cursor-pointer select-none"
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/trends/${encodeURIComponent(row.our_sku)}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-blue-600 hover:underline line-clamp-2 min-w-0"
        >
          {row.product_name}
        </Link>
        {row._incomplete && (
          <span
            className="mt-1 h-2 w-2 rounded-full bg-amber-400 flex-shrink-0"
            title="Not all competitors scraped this product"
          />
        )}
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-xs font-mono text-gray-400">
        <span>{row.our_sku}</span>
        {row.key_line && <KeyLineBadge className="font-sans" />}
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Ours
          </div>
          <OurPriceCell row={{ original: row }} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Min
          </div>
          <MinMaxPriceCell
            priceStatus={row.min_competitor}
            diffPct={row.min_competitor_diff_pct}
            url={row.min_competitor_url}
          />
          {minDomain && (
            <div className="text-[11px] text-gray-500 truncate">
              {domainName(minDomain)}
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            Max
          </div>
          <MinMaxPriceCell
            priceStatus={row.max_competitor}
            diffPct={row.max_competitor_diff_pct}
            url={row.max_competitor_url}
          />
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-400">
        <svg
          className={`h-3.5 w-3.5 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
        <span>
          {priced} priced competitor{priced === 1 ? '' : 's'}
        </span>
      </div>

      {expanded && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-gray-100 pt-2">
          {competitors.map((domainId) => (
            <div
              key={domainId}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="text-xs text-gray-500 truncate">
                {domainName(domainId)}
              </span>
              <CompetitorPriceCell
                priceStatus={row.competitor_prices?.[domainId]}
                ourPriceValue={ourVal}
                url={row.competitor_urls?.[domainId]}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The virtualised card list with its header (count + sort control).
 *
 * @param {object} props
 * @param {Array}  props.rows - Enriched ComparisonRow objects.
 * @param {string[]} props.competitors - Filtered competitor domain ids.
 * @param {number} props.totalCount - Total matching rows before limit.
 * @param {string} props.sortBy - Active sort key.
 * @param {string} props.sortDir - 'asc' | 'desc'.
 * @param {(key: string, dir: string) => void} props.onSortSet - Sets
 *   both sort key and direction (persisted by ComparisonPage).
 */
export default function MobileComparisonList({
  rows,
  competitors,
  totalCount,
  sortBy,
  sortDir,
  onSortSet,
}) {
  const scrollRef = useRef(null);
  const [expandedSkus, setExpandedSkus] = useState(() => new Set());

  const toggleExpanded = (sku) => {
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CARD_ESTIMATE,
    overscan: 8,
  });

  // Scroll retention, same pattern as the desktop table but with an
  // independent key: crossing the breakpoint must not clobber the
  // desktop offsets (and vice versa).
  const restoredRef = useRef(false);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const save = () => {
      if (!el.isConnected) return;
      saveScrollPosition(SCROLL_MEMORY_KEY, { top: el.scrollTop, left: 0 });
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      el.removeEventListener('scroll', save);
      if (restoredRef.current) save();
    };
  }, []);

  const virtualItems = virtualizer.getVirtualItems();

  useLayoutEffect(() => {
    if (restoredRef.current) return;
    if (rows.length === 0 || virtualItems.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    restoredRef.current = true;
    const saved = getScrollPosition(SCROLL_MEMORY_KEY);
    if (saved) el.scrollTop = saved.top;
  }, [rows.length, virtualItems.length]);

  const sortValue = useMemo(
    () => (SORT_OPTIONS.some((o) => o.value === sortBy)
      ? sortBy
      : 'product_name'),
    [sortBy],
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0">
      {/* Header: count + sort control (cards have no column headers) */}
      <div className="flex items-center justify-between gap-2 px-1 py-2 text-sm text-gray-500">
        <span className="whitespace-nowrap">
          <span className="font-medium text-gray-700">{rows.length}</span>
          {totalCount !== rows.length && (
            <>
              {' '}of{' '}
              <span className="font-medium text-gray-700">{totalCount}</span>
            </>
          )}
          {' '}products
        </span>
        <div className="flex items-center gap-1.5">
          <select
            value={sortValue}
            onChange={(e) => onSortSet(e.target.value, sortDir)}
            className="rounded-md border-gray-300 py-1 pl-2 pr-7 text-sm
                       focus:border-blue-500 focus:ring-blue-500"
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() =>
              onSortSet(sortValue, sortDir === 'asc' ? 'desc' : 'asc')
            }
            className="rounded-md border border-gray-300 bg-white px-2 py-1
                       text-gray-600 hover:bg-gray-50"
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            aria-label="Toggle sort direction"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Virtualised card list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto border border-gray-200 rounded-lg
                   bg-white shadow-sm"
      >
        <div
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualItems.map((item) => {
            const row = rows[item.index];
            return (
              <div
                key={row.our_sku}
                data-index={item.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 w-full"
                style={{ transform: `translateY(${item.start}px)` }}
              >
                <ProductCard
                  row={row}
                  competitors={competitors}
                  expanded={expandedSkus.has(row.our_sku)}
                  onToggle={() => toggleExpanded(row.our_sku)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
